import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import "./styles.css";
import Header from "../../components/Header/Header";
import BottomNav from "../../components/BottomNav/BottomNav";
import SponsorshipSection from "../../components/SponsorshipSection/SponsorshipSection";
import FooterLinks from "../../components/FooterLinks/FooterLinks";
import Splash from "../../common/Splash";
import NoData from "../../common/NoData";

/* ================= MENU DATA ================= */

const menuData = [
  { title: "Favorites", type: "FAVORITES", icon: "⭐" },
  { title: "Casino", type: "LIVE", icon: "♠️" },
  { title: "Slots", type: "SLOT", icon: "🎰" },
  { title: "Crash", type: "EGAME", icon: "🚀" },
  { title: "Table", type: "TABLE", icon: "🟢" },
  { title: "Fishing", type: "FH", icon: "🐟" },
  { title: "Arcade", type: "BINGO", icon: "🕹️" },
  { title: "Lottery", type: "LOTTO", icon: "🎟️" },
];

/* ================= GAME CARD ================= */

function GameCard({ game }) {
  const navigate = useNavigate();
  const origin = window.location.origin;
  const [isLoading, setIsLoading] = useState(false);
  const isLoggedIn = !!localStorage.getItem("token");

  const imgUrl = (game) => {
    let slug = game?.slug;
    if (!slug) {
      let code = game.code?.replace(/\s/g, "_").toLowerCase();
      let name = game.name
        ?.replace(/[!?/']/g, "")
        .replace(/\s/g, "_")
        .toLowerCase();
      slug = `${code}_${name}.png`;
    }
    return `https://api2b.pro/poster/${slug}`;
  };

  const handleGameClick = async () => {
    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem("token");
      const remote_id = localStorage.getItem("remote_id");

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/inr-game-run`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            userId: remote_id,
            externalURL: origin,
            gameCode: game.uid,
            gameType: game.type,
            platform: game.platform,
          }),
        }
      );

      const data = await response.json();

      if (response.ok && data?.url) {
        navigate("/gamerun", {
          state: { iframeUrl: data.url },
        });
      }
    } catch (error) {
      console.error("Game launch error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="game-card" onClick={handleGameClick}>
      <div className="game-bg"></div>
      <img src={imgUrl(game)} alt={game.name} loading="lazy" />
    </div>
  );
}

/* ================= MAIN COMPONENT ================= */

function GamesCasino({ globalData }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const type = searchParams.get("type");
  const system = searchParams.get("system");

  const [games, setGames] = useState([]);
  const [filteredGames, setFilteredGames] = useState([]);
  const [visibleGames, setVisibleGames] = useState(20);
  const [isLoading, setIsLoading] = useState(true);

  const [showCategoryMenu, setShowCategoryMenu] = useState(false);

  /* ================= FETCH GAMES ================= */

  const fetchGames = useCallback(async () => {
    if (!type || !system) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/get-games?type=${type}&system=${system}`
      );

      const data = await response.json();

      if (response.ok) {
        setGames(data.games || []);
        setFilteredGames(data.games || []);
        setVisibleGames(20);
      }
    } catch (error) {
      console.error("Error fetching games:", error);
    } finally {
      setIsLoading(false);
    }
  }, [type, system]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames]);

  /* ================= CATEGORY CHANGE ================= */

  const handleCategoryChange = (selectedType) => {
    setShowCategoryMenu(false);

    navigate(`/casino?type=${selectedType}&system=${system}`);
  };

  /* ================= LOAD MORE ================= */

  const handleLoadMore = () => {
    setVisibleGames((prev) =>
      Math.min(prev + 20, filteredGames.length)
    );
  };

  const currentGames = filteredGames.slice(0, visibleGames);
  const totalGames = filteredGames.length;
  const progressPercentage =
    totalGames > 0 ? (visibleGames / totalGames) * 100 : 0;
  const hasMore = visibleGames < totalGames;

  if (isLoading) {
    return <Splash />;
  }

  return (
    <div className="app-container">
      <Header />

      <header className="header-cas">
        <div className="header-cas-left">
          <span className="header-cas-icon">🎰</span>

          <h1
            className="header-cas-title"
            onClick={() => setShowCategoryMenu(!showCategoryMenu)}
            style={{ cursor: "pointer" }}
          >
            {menuData.find((m) => m.type === type)?.title || "Casino"}
            <svg width="16" height="16" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </h1>
        </div>

        <div className="header-cas-right">
          <button className="icon-button">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>

          <button className="icon-button">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </button>

          <button className="icon-button">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* ===== DROPDOWN SIDEBAR ===== */}
      {showCategoryMenu && (
        <div className="casino-category-dropdown">
          <div className="casino-category-menu">
            {menuData.map((item) => (
              <div
                key={item.type}
                className={`casino-category-item ${
                  item.type === type ? "active" : ""
                }`}
                onClick={() => handleCategoryChange(item.type)}
              >
                <span className="casino-category-icon">
                  {item.icon}
                </span>
                <span className="casino-category-text">
                  {item.title}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <main className="main-contents">
        <div className="games-grid-casino">
          {currentGames.length > 0 ? (
            currentGames.map((game) => (
              <GameCard key={game.id || game.uid} game={game} />
            ))
          ) : (
            <NoData />
          )}
        </div>

        {hasMore && (
          <div className="load-more-section">
            <button
              className={`load-more-btn ${isLoading ? "loading" : ""}`}
              onClick={handleLoadMore}
              disabled={isLoading}
            >
              Load more
            </button>

            <div className="progress-bar-container">
              <div
                className="progress-bar-fill"
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>

            <p className="games-count-text">
              Shown {visibleGames} of {totalGames} games
            </p>
          </div>
        )}

        <FooterLinks />
        <SponsorshipSection />
      </main>

      <BottomNav marketBlock={globalData.marketBlock || []} />
    </div>
  );
}

export default GamesCasino;
